import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminNotificationAudienceGroupList,
  fetchYowanAdminNotificationRecipientList,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const RECIPIENTS_PAGE_SIZE = 30;
const AUDIENCE_GROUP_PAGE_SIZE = 100;

const recipientSortOptions = [
  { value: 'email-asc', label: 'メール昇順' },
  { value: 'email-desc', label: 'メール降順' },
  { value: 'name-asc', label: '表示名昇順' },
  { value: 'updated-desc', label: '更新日が新しい順' },
  { value: 'status-asc', label: '状態昇順' },
];

const audienceGroupSortOptions = [
  { value: 'group-asc', label: 'グループ昇順' },
  { value: 'kind-asc', label: '種別昇順' },
  { value: 'policy-desc', label: '関連ルールが多い順' },
  { value: 'visibility-asc', label: '解決方法昇順' },
];

const categoryFilterLabels = {
  news: 'お知らせ配信',
  blog: 'ブログ配信',
};

const indexColumnStyle = {
  width: '2.5rem',
  minWidth: '2.5rem',
  whiteSpace: 'nowrap',
};

const emailColumnStyle = {
  width: '20rem',
  minWidth: '20rem',
};

const preferenceColumnStyle = {
  width: '18rem',
  minWidth: '18rem',
};

const sourceColumnStyle = {
  width: '18rem',
  minWidth: '18rem',
};

const statusColumnStyle = {
  width: '7rem',
  minWidth: '7rem',
  whiteSpace: 'nowrap',
};

const updatedColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
};

const actionColumnStyle = {
  width: '6rem',
  minWidth: '6rem',
};

const tableStyle = {
  minWidth: '84rem',
};

const audienceGroupTableStyle = {
  minWidth: '78rem',
};

const audienceGroupColumnStyle = {
  width: '17rem',
  minWidth: '17rem',
};

const audienceKindColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
  whiteSpace: 'nowrap',
};

const audienceResolutionColumnStyle = {
  minWidth: '24rem',
};

const audiencePolicyColumnStyle = {
  width: '12rem',
  minWidth: '12rem',
};

const audienceActionColumnStyle = {
  width: '13rem',
  minWidth: '13rem',
};

const getSortableDateValue = (value) => {
  if (!value || value === '-') {
    return 0;
  }

  const timestamp = Date.parse(String(value).replace(' ', 'T'));
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortRecipientRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'email-desc') {
      return right.email.localeCompare(left.email, 'en');
    }

    if (sortKey === 'name-asc') {
      return left.displayName.localeCompare(right.displayName, 'ja');
    }

    if (sortKey === 'updated-desc') {
      return getSortableDateValue(right.updatedAt) - getSortableDateValue(left.updatedAt);
    }

    if (sortKey === 'status-asc') {
      return Number(right.isActive) - Number(left.isActive);
    }

    return left.email.localeCompare(right.email, 'en');
  });

  return nextRows;
};

const sortAudienceGroupRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'kind-asc') {
      return `${left.groupKind}${left.groupLabel}`.localeCompare(`${right.groupKind}${right.groupLabel}`, 'ja');
    }

    if (sortKey === 'policy-desc') {
      return right.relatedPolicyCount - left.relatedPolicyCount;
    }

    if (sortKey === 'visibility-asc') {
      return `${left.recipientVisibilityLabel}${left.groupLabel}`.localeCompare(
        `${right.recipientVisibilityLabel}${right.groupLabel}`,
        'ja',
      );
    }

    return left.groupLabel.localeCompare(right.groupLabel, 'ja');
  });

  return nextRows;
};

const filterAudienceGroupRows = (rows, query) => {
  const normalizedQuery = query.trim().toLowerCase();

  if (!normalizedQuery) {
    return rows;
  }

  return rows.filter((row) => [
    row.groupKey,
    row.groupLabel,
    row.groupKind,
    row.audienceType,
    row.audienceKey,
    row.description,
    row.resolutionSummary,
    row.recipientVisibilityLabel,
    row.preferenceCategory,
    row.relatedEventKeys.join(' '),
  ].some((value) => String(value || '').toLowerCase().includes(normalizedQuery)));
};

const AdminNotificationRecipientsListPage = () => {
  const content = yowanPageContent['/admin/notifications/recipients'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const requestedTab = searchParams.get('tab') === 'list' ? 'list' : 'groups';
  const initialCategory = searchParams.get('category_key') || '';
  const initialGroupKey = searchParams.get('group_key') || '';
  const [activeTab, setActiveTab] = useState(requestedTab);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [audienceGroupRows, setAudienceGroupRows] = useState([]);
  const [audienceGroupTotalCount, setAudienceGroupTotalCount] = useState(0);
  const [isAudienceGroupLoading, setIsAudienceGroupLoading] = useState(true);
  const [audienceGroupLoadError, setAudienceGroupLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [audienceGroupQuery, setAudienceGroupQuery] = useState(initialGroupKey);
  const [sortKey, setSortKey] = useState('email-asc');
  const [audienceGroupSortKey, setAudienceGroupSortKey] = useState('group-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [categoryKey, setCategoryKey] = useState(initialCategory);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    setActiveTab(requestedTab);
    setCategoryKey(initialCategory);
    setAudienceGroupQuery(initialGroupKey);
    setCurrentPage(1);
  }, [initialCategory, initialGroupKey, requestedTab]);

  useEffect(() => {
    if (activeTab !== 'list') {
      return undefined;
    }

    let isMounted = true;
    const query = searchQuery.trim();
    const fetchTimer = window.setTimeout(() => {
      setIsLoading(true);
      setLoadError('');

      fetchYowanAdminNotificationRecipientList({
        page: currentPage,
        pageSize: RECIPIENTS_PAGE_SIZE,
        query,
        categoryKey,
        isActive: activeFilter === 'all' ? undefined : activeFilter === 'active',
      })
        .then((payload) => {
          if (isMounted) {
            setRows(payload.results);
            setTotalCount(payload.count);
          }
        })
        .catch(() => {
          if (isMounted) {
            setRows([]);
            setTotalCount(0);
            setLoadError('通知宛先を読み込めませんでした。');
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        });
    }, 180);

    return () => {
      isMounted = false;
      window.clearTimeout(fetchTimer);
    };
  }, [activeFilter, activeTab, categoryKey, currentPage, searchQuery]);

  useEffect(() => {
    if (activeTab !== 'groups') {
      return undefined;
    }

    let isMounted = true;
    setIsAudienceGroupLoading(true);
    setAudienceGroupLoadError('');

    fetchYowanAdminNotificationAudienceGroupList({ pageSize: AUDIENCE_GROUP_PAGE_SIZE })
      .then((payload) => {
        if (isMounted) {
          setAudienceGroupRows(payload.results);
          setAudienceGroupTotalCount(payload.count);
        }
      })
      .catch(() => {
        if (isMounted) {
          setAudienceGroupRows([]);
          setAudienceGroupTotalCount(0);
          setAudienceGroupLoadError('宛先グループを読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsAudienceGroupLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [activeTab]);

  if (!content) {
    return null;
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / RECIPIENTS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * RECIPIENTS_PAGE_SIZE;
  const visibleRows = sortRecipientRows(rows, sortKey);
  const visibleAudienceGroupRows = sortAudienceGroupRows(
    filterAudienceGroupRows(audienceGroupRows, audienceGroupQuery),
    audienceGroupSortKey,
  );
  const categoryContextText = categoryKey
    ? `${categoryFilterLabels[categoryKey] || categoryKey}の受信設定を持つ宛先候補を表示しています。`
    : '通知可能な宛先台帳を表示しています。';
  const audienceGroupMetaText = `${audienceGroupTotalCount}件中 ${visibleAudienceGroupRows.length}件を表示`;

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="dispatches" to={appendYowanPreviewQueryIfNeeded('/admin/notifications', preservedDomainSearch)}>
            通知履歴へ
          </Link>,
          <Link key="policies" to={appendYowanPreviewQueryIfNeeded('/admin/notifications/policies', preservedDomainSearch)}>
            通知ルールへ
          </Link>,
        ]}
      />

      <div
        className="yowan-admin-tabs"
        role="tablist"
        aria-label="通知宛先表示"
      >
        <button
          type="button"
          className={`yowan-admin-tabs__tab${
            activeTab === 'groups' ? ' yowan-admin-tabs__tab--active' : ''
          }`}
          aria-selected={activeTab === 'groups'}
          role="tab"
          onClick={() => setActiveTab('groups')}
        >
          宛先グループ
        </button>
        <button
          type="button"
          className={`yowan-admin-tabs__tab${
            activeTab === 'list' ? ' yowan-admin-tabs__tab--active' : ''
          }`}
          aria-selected={activeTab === 'list'}
          role="tab"
          onClick={() => setActiveTab('list')}
        >
          宛先リスト
        </button>
      </div>

      {activeTab === 'groups' ? (
        <>
          <AdminListToolbar
            searchValue={audienceGroupQuery}
            onSearchChange={(event) => setAudienceGroupQuery(event.target.value)}
            sortValue={audienceGroupSortKey}
            onSortChange={(event) => setAudienceGroupSortKey(event.target.value)}
            metaText={audienceGroupMetaText}
            searchPlaceholder="グループ / 種別 / イベント"
            sortOptions={audienceGroupSortOptions}
          />
          <p className="yowan-admin-toolbar__meta">
            通知全体で参照される宛先グループと、送信時の解決方法を表示しています。
          </p>

          <div className="yowan-admin-resource-table-wrap c-surface-content">
            <table className="yowan-admin-resource-table" style={audienceGroupTableStyle}>
              <thead>
                <tr>
                  <th scope="col" style={audienceGroupColumnStyle}>グループ</th>
                  <th scope="col" style={audienceKindColumnStyle}>種別</th>
                  <th scope="col" style={audienceResolutionColumnStyle}>解決方法</th>
                  <th scope="col" style={audiencePolicyColumnStyle}>関連ルール</th>
                  <th scope="col" style={audienceActionColumnStyle}>確認</th>
                </tr>
              </thead>
              <tbody>
                {visibleAudienceGroupRows.map((row) => (
                  <tr key={row.groupKey}>
                    <td style={audienceGroupColumnStyle}>
                      <p className="yowan-admin-resource-table__title">{row.groupLabel}</p>
                      <p className="yowan-admin-resource-table__subline">{row.groupKey}</p>
                    </td>
                    <td style={audienceKindColumnStyle}>
                      <p className="yowan-admin-resource-table__title">{row.groupKind || '-'}</p>
                      <p className="yowan-admin-resource-table__subline">{row.audienceType || '-'}</p>
                    </td>
                    <td style={audienceResolutionColumnStyle}>
                      <p className="yowan-admin-resource-table__title">{row.recipientVisibilityLabel}</p>
                      <p className="yowan-admin-resource-table__subline">{row.description || '-'}</p>
                      <p className="yowan-admin-resource-table__subline">{row.resolutionSummary || '-'}</p>
                    </td>
                    <td style={audiencePolicyColumnStyle}>
                      <p className="yowan-admin-resource-table__title">{row.relatedPolicyCount}件</p>
                      <p className="yowan-admin-resource-table__subline">
                        {row.relatedEventKeys.length ? row.relatedEventKeys.join(' / ') : '-'}
                      </p>
                    </td>
                    <td style={audienceActionColumnStyle}>
                      {row.canListRecipients && row.recipientListHref ? (
                        <Link to={appendYowanPreviewQueryIfNeeded(row.recipientListHref, preservedDomainSearch)}>
                          宛先リスト
                        </Link>
                      ) : (
                        <span className="yowan-admin-resource-table__subline">通知履歴で確認</span>
                      )}
                    </td>
                  </tr>
                ))}
                {!visibleAudienceGroupRows.length ? (
                  <tr>
                    <td colSpan="5">
                      {isAudienceGroupLoading
                        ? <LoadingSpinner size="sm" />
                        : audienceGroupLoadError || '宛先グループはまだありません。'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <>
          <AdminListToolbar
            searchValue={searchQuery}
            onSearchChange={(event) => {
              setSearchQuery(event.target.value);
              setCurrentPage(1);
            }}
            sortValue={sortKey}
            onSortChange={(event) => setSortKey(event.target.value)}
            metaText={`${totalCount}件中 ${visibleRows.length}件を表示${
              totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
            }`}
            searchPlaceholder="メールアドレス / 表示名"
            sortOptions={recipientSortOptions}
          />

          <div className="yowan-admin-toolbar">
            <label className="c-form__field yowan-admin-toolbar__field">
              <span className="c-form__label">カテゴリ</span>
              <select
                className="c-form__control yowan-admin-toolbar__sort"
                value={categoryKey}
                onChange={(event) => {
                  setCategoryKey(event.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">すべて</option>
                <option value="news">お知らせ</option>
                <option value="blog">ブログ</option>
              </select>
            </label>
            <label className="c-form__field yowan-admin-toolbar__field">
              <span className="c-form__label">状態</span>
              <select
                className="c-form__control yowan-admin-toolbar__sort"
                value={activeFilter}
                onChange={(event) => {
                  setActiveFilter(event.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="all">すべて</option>
                <option value="active">有効</option>
                <option value="inactive">無効</option>
              </select>
            </label>
          </div>

          <p className="yowan-admin-toolbar__meta">{categoryContextText}</p>

          <div className="yowan-admin-resource-table-wrap c-surface-content">
            <table className="yowan-admin-resource-table" style={tableStyle}>
              <thead>
                <tr>
                  <th scope="col" style={indexColumnStyle}>#</th>
                  <th scope="col" style={emailColumnStyle}>宛先</th>
                  <th scope="col" style={preferenceColumnStyle}>受信設定</th>
                  <th scope="col" style={sourceColumnStyle}>由来</th>
                  <th scope="col" style={statusColumnStyle}>状態</th>
                  <th scope="col" style={updatedColumnStyle}>更新日</th>
                  <th scope="col" style={actionColumnStyle}>操作</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, index) => (
                  <tr key={row.id}>
                    <td style={indexColumnStyle}>{startIndex + index + 1}</td>
                    <td style={emailColumnStyle}>
                      <p className="yowan-admin-resource-table__title">{row.email || row.maskedEmail || '-'}</p>
                      <p className="yowan-admin-resource-table__subline">{row.displayName || row.maskedEmail || '-'}</p>
                    </td>
                    <td style={preferenceColumnStyle}>{row.preferenceSummary}</td>
                    <td style={sourceColumnStyle}>{row.sourceSummary}</td>
                    <td style={statusColumnStyle}>
                      <span
                        className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${
                          row.isActive ? 'published' : 'draft'
                        }`}
                      >
                        {row.isActive ? '有効' : '無効'}
                      </span>
                    </td>
                    <td style={updatedColumnStyle}>{row.updatedAt}</td>
                    <td style={actionColumnStyle}>
                      <Link to={appendYowanPreviewQueryIfNeeded(row.detailHref, preservedDomainSearch)}>詳細</Link>
                    </td>
                  </tr>
                ))}
                {!visibleRows.length ? (
                  <tr>
                    <td colSpan="7">
                      {isLoading ? <LoadingSpinner size="sm" /> : loadError || '通知宛先はまだありません。'}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>

          <AdminListPagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}
    </AdminBlogShell>
  );
};

export default AdminNotificationRecipientsListPage;
